import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, X, Trash, Star, Article, ChatCircleDots, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'articles', label: 'Pending Articles', icon: Article },
  { key: 'reviews', label: 'Pending Reviews', icon: ChatCircleDots },
  { key: 'reports', label: 'Reports', icon: Warning },
];

export default function CmsModeration() {
  const [data, setData] = useState({ articles: [], reviews: [], reports: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('articles');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/cms/moderation/pending`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error('Failed to load moderation queue'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doAction = async (kind, id, action, reason = null) => {
    if (action === 'delete' && !confirm('Delete permanently?')) return;
    if (action === 'reject' && !reason) {
      reason = prompt('Reason for rejection?') || 'No reason provided';
    }
    try {
      await axios.post(`${API}/api/admin/cms/moderation/${kind}/${id}/${action}`, { reason }, { withCredentials: true });
      toast.success(`${kind} ${action}d`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const items = data[tab] || [];

  return (
    <div data-testid="cms-moderation">
      <div className="mb-4">
        <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Moderation Center</h2>
        <p className="text-sm text-muted-foreground">{data.total} items awaiting review</p>
      </div>

      <div className="flex gap-2 mb-4 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`cms-moderation-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            <t.icon size={16} />
            {t.label}
            <span className="text-xs bg-muted rounded-full px-2 py-0.5">{(data[t.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="cms-moderation-list">
        {loading ? (
          <p className="text-center py-10 text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Check size={48} className="text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>All caught up</p>
            <p className="text-sm text-muted-foreground">No {tab} pending review.</p>
          </div>
        ) : items.map((item) => (
          <div key={item.id} className="bg-card border border-border rounded-lg p-4" data-testid={`mod-item-${item.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{item.kind}</span>
                  {item.rating && <span className="text-xs text-amber-600">★ {item.rating}</span>}
                  {item.category && <span className="text-xs text-muted-foreground">· {item.category}</span>}
                </div>
                <p className="font-medium truncate">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-1">By {item.author_name || item.reporter_id || 'Unknown'} · {(item.created_at || '').slice(0,10)}</p>
                {item.preview && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{item.preview}</p>}
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                {tab !== 'reports' && (
                  <>
                    <button
                      onClick={() => doAction(tab === 'articles' ? 'article' : 'review', item.id, 'approve')}
                      data-testid={`mod-approve-${item.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 hover:bg-emerald-200 px-3 py-1.5 rounded-full"
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => doAction(tab === 'articles' ? 'article' : 'review', item.id, 'reject')}
                      data-testid={`mod-reject-${item.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/40 hover:bg-orange-200 px-3 py-1.5 rounded-full"
                    >
                      <X size={12} /> Reject
                    </button>
                  </>
                )}
                {tab === 'articles' && (
                  <button
                    onClick={() => doAction('article', item.id, 'feature')}
                    className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-3 py-1.5 rounded-full"
                  >
                    <Star size={12} /> Feature
                  </button>
                )}
                {tab === 'reports' && (
                  <button
                    onClick={() => doAction('report', item.id, 'reject')}
                    className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full"
                  >
                    <Check size={12} /> Resolve
                  </button>
                )}
                <button
                  onClick={() => doAction(tab === 'articles' ? 'article' : tab === 'reviews' ? 'review' : 'report', item.id, 'delete')}
                  data-testid={`mod-delete-${item.id}`}
                  className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 dark:bg-red-950/40 hover:bg-red-200 px-3 py-1.5 rounded-full"
                >
                  <Trash size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
